import { useState } from "react";

import i18next from "i18next";
import moment from "moment";
import { IoGlobeOutline, IoChevronDown, IoLogOutOutline, IoPersonOutline, IoChatbubblesOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";

import defaultCurrencies from "../../../config/defaultCurrencies";
import defaultLanguages from "../../../config/defaultLanguages";

import { setAppConfigs } from "../../../store/appConfigsSlice";

import { useAuth } from "../../../context/auth";

import useWindowDimensions from "../../../hooks/useWindowDimensions";

import { DropdownComponent } from "../../ui";

import HeaderSearchOrderComponent from "../HeaderSearchOrderComponent";

import {
  Avatar,
  AvatarImage,
  BalanceAmount,
  BalanceLabel,
  BalanceSection,
  CreditText,
  DropdownItem,
  DropdownItemLink,
  DropdownItems,
  DropdownSectionTitle,
  LanguageButton,
  LanguageText,
  LogoutButton,
  SettingsBlock,
  SupportIconLink,
  TextLanguage,
  UserIconButton,
  UserInfoSection,
  UserName,
  Wrapper,
} from "./style";
import { SupportUnreadBadge } from "../../support";

const HeaderNavigationComponent = () => {
  const { changeLanguage, language, t } = i18next;

  const { width } = useWindowDimensions();

  const { availableCurrencies, availableLanguages, currency } = useSelector(state => state.appConfigs);
  const dispatch = useDispatch();
  const { isAuth, onLogout, user } = useAuth();

  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const handleSelectCurrency = currency => {
    // JSON, как читает ConfigurationProvider (сырая строка не переживала перезагрузку)
    localStorage.setItem("currency", JSON.stringify(currency));
    dispatch(setAppConfigs({ currency }));
    setShowSettingsDropdown(false);
  };

  const handleSelectLanguage = language => {
    switchLang(language);
    setShowSettingsDropdown(false);
  };

  const currencies = () => {
    return availableCurrencies.map(value => {
      return (
        <DropdownItem key={`currency-${value}`} onClick={() => handleSelectCurrency(value)}>
          <TextLanguage className={value === currency ? "active" : ""}>
            {defaultCurrencies[language]?.[value] ?? value}
          </TextLanguage>
        </DropdownItem>
      );
    });
  };

  const renderLanguages = () => {
    return availableLanguages.map(value => {
      return (
        <DropdownItem key={`languages-${value}`} onClick={() => handleSelectLanguage(value)}>
          <TextLanguage className={value === language ? "active" : ""}>
            {defaultLanguages[language][value]}
          </TextLanguage>
        </DropdownItem>
      );
    });
  };

  const switchLang = lang => {
    changeLanguage(lang);
    moment.updateLocale(lang === "tj" ? "tg" : lang, { week: { dow: 1 } });
    localStorage.setItem("language", lang);
    document.documentElement.setAttribute("lang", lang);
    dispatch(setAppConfigs({ language: lang }));
  };

  const handleLogout = () => {
    onLogout();
    setShowUserDropdown(false);
  };

  const getUserInitials = () => {
    const firstName = user?.first_name || "";
    const lastName = user?.last_name || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatBalance = balance => {
    return new Intl.NumberFormat("en-US").format(balance || 0);
  };

  return (
    <Wrapper>
      {!isAuth && width > 768 && <HeaderSearchOrderComponent />}

      <SettingsBlock>
        <LanguageButton onClick={() => setShowSettingsDropdown(true)}>
          <IoGlobeOutline />
          {width > 768 && <LanguageText>{language?.toUpperCase()}</LanguageText>}
          <IoChevronDown className="chevron" />
        </LanguageButton>

        {showSettingsDropdown && (
          <DropdownComponent
            show={showSettingsDropdown}
            onClickOutside={() => setShowSettingsDropdown(false)}
            width={180}
          >
            <DropdownSectionTitle>{t("currency")}</DropdownSectionTitle>
            <DropdownItems>{currencies()}</DropdownItems>

            <DropdownSectionTitle>{t("language")}</DropdownSectionTitle>
            <DropdownItems>{renderLanguages()}</DropdownItems>
          </DropdownComponent>
        )}
      </SettingsBlock>

      {/* на мобильном вкладки шапки (и «Поддержка» в них) скрыты — без этой иконки гостю негде создать заявку */}
      {width <= 768 && (
        <SupportIconLink to="/support" aria-label={t("support_nav")} title={t("support_nav")}>
          <IoChatbubblesOutline />
          <span className="support-badge">
            <SupportUnreadBadge />
          </span>
        </SupportIconLink>
      )}

      {isAuth && (
        <SettingsBlock>
          <UserInfoSection onClick={() => setShowUserDropdown(true)}>
            {width > 768 && (
              <BalanceSection>
                <UserName>
                  {user?.first_name} {user?.last_name}
                </UserName>
                <BalanceLabel>
                  <BalanceAmount className="positive">
                    {formatBalance(user?.balance)} {user?.currency}
                  </BalanceAmount>
                </BalanceLabel>
                <CreditText>
                  {t("credit_limit")}: {formatBalance(user?.credit_limit)} {user?.currency}
                </CreditText>
              </BalanceSection>
            )}
            <Avatar>
              {user?.avatar ? <AvatarImage src={user.avatar} alt={user?.first_name} /> : getUserInitials()}
            </Avatar>
          </UserInfoSection>

          {showUserDropdown && (
            <DropdownComponent show={showUserDropdown} onClickOutside={() => setShowUserDropdown(false)} width={220}>
              {width <= 768 && (
                <>
                  <DropdownSectionTitle className="user-info">
                    {user?.first_name} {user?.last_name}
                  </DropdownSectionTitle>
                  <DropdownSectionTitle className="balance">
                    {formatBalance(user?.balance)} {user?.currency}
                  </DropdownSectionTitle>
                </>
              )}

              <DropdownItems>
                <DropdownItemLink to="/orders" onClick={() => setShowUserDropdown(false)}>
                  {t("orders")}
                </DropdownItemLink>

                <DropdownItemLink to="/support" onClick={() => setShowUserDropdown(false)}>
                  {t("support_title")}
                  <SupportUnreadBadge inline />
                </DropdownItemLink>

                <DropdownItemLink to="/transactions" onClick={() => setShowUserDropdown(false)}>
                  {t("transactions")}
                </DropdownItemLink>

                <DropdownItemLink to="/user/balance" onClick={() => setShowUserDropdown(false)}>
                  {t("recharging_balance")}
                </DropdownItemLink>
              </DropdownItems>

              <LogoutButton onClick={handleLogout}>
                <IoLogOutOutline />
                {t("log_out")}
              </LogoutButton>
            </DropdownComponent>
          )}
        </SettingsBlock>
      )}

      {!isAuth && (
        <SettingsBlock>
          <UserIconButton onClick={() => setShowUserDropdown(true)}>
            <IoPersonOutline />
          </UserIconButton>

          {showUserDropdown && (
            <DropdownComponent show={showUserDropdown} onClickOutside={() => setShowUserDropdown(false)} width={200}>
              <DropdownItems>
                <DropdownItemLink to="/auth" onClick={() => setShowUserDropdown(false)}>
                  {t("log_in")}
                </DropdownItemLink>

                <DropdownItemLink to="/registration" onClick={() => setShowUserDropdown(false)}>
                  {t("registration")}
                </DropdownItemLink>
              </DropdownItems>
            </DropdownComponent>
          )}
        </SettingsBlock>
      )}
    </Wrapper>
  );
};

export default HeaderNavigationComponent;
